// The Alabama FCPA summary-first report preview — AL gets the same polish the federal Form 3
// (Form3SummaryView) and Virginia COMET (VaCometSummaryView) reports got: a Statement-of-funds
// card + a horizontal cash-on-hand bridge, then each schedule as an expandable section — instead
// of the older flat stack of full-width tables. It renders for an Alabama (portal-only) report,
// which carries neither `federalSummary` nor `vaSchedules`.
//
// Every DISPLAYED total is read off the report-detail DTO the file path already produced: the
// cash bridge comes straight from `report.cashBridge` (the same term-set as the filed ending
// cash — see FinanceCashMath.ComputeBridge — so the strip cannot drift a cent from the file), the
// schedule subtotals sum only the rows WITHIN a schedule, and the "will be on the filing" count
// reads the server-authored will-file projection (`preview.willFile`, ADR-034) rather than a
// client-derived count that drifts on amendments / processor-status drift.
//
// The editable schedules (A contributions, B expenditures) reuse the report-detail's own editable
// items, so click-through-to-edit still works on a pre-filing (Ready) report; a Filed snapshot
// passes null edit handlers → read-only rows. Excluded / pending / voided rows collapse into one
// "in this period, not on this filing" section, mirroring the federal + VA off-filing block.
import { useState, type ReactNode } from 'react';
import { ChevronRight, Pencil, AlertTriangle } from 'lucide-react';
import { PreflightCopy, assertNever, ReportStatus, FilingOrigin, electionDesignationLabel, isOnReportInclusion } from '@signalvote/shared';
import type {
  ComplianceReportDetailDto,
  FilingPreviewDto,
  ContributionReportItem,
  ExpenditureReportItem,
  ReportRowInclusion,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount, formatDate } from '../utils';
import { FilingLockCopy } from './filingLockCopy';
import { ResendTransactionAction } from './ResendTransactionAction';
import { ReportedRowBadge } from './ReportedRowBadge';

interface AlabamaSummaryViewProps {
  report: ComplianceReportDetailDto;
  // Non-null only on a pre-filing (Ready) report — drives the Schedule A dual-count note.
  preview: FilingPreviewDto | null;
  portalName: string;
  requiresEmployer: boolean;
  // Non-null only on a pre-filing (Ready) report; a Filed snapshot passes null → read-only rows.
  onEditContribution: ((contributionId: string) => void) | null;
  onEditExpenditure: ((expenditureId: string) => void) | null;
  onFixMissingInfo: (contactId: string) => void;
}

export function AlabamaSummaryView({
  report,
  preview,
  portalName,
  requiresEmployer,
  onEditContribution,
  onEditExpenditure,
  onFixMissingInfo,
}: AlabamaSummaryViewProps) {
  const isExternallyFiled = report.filingOrigin === FilingOrigin.ExternalPortal;
  const isReady = report.status === ReportStatus.Ready;

  // "on the report" is Included PLUS already-reported rows folded back into an open
  // report (the AL upload flips them to Reported before the period is filed). The schedule +
  // "Contributions received" totals reflect the full period; the "Will be on the filing" count
  // below stays the server will-file projection, so the two never contradict.
  const includedContributions = report.contributionItems.filter((i) => isOnReportInclusion(i.inclusion));
  const itemizedContributions = includedContributions.filter((i) => i.isItemized);
  const nonItemizedContributions = includedContributions.filter((i) => !i.isItemized);
  const includedExpenditures = report.expenditureItems.filter((i) => isOnReportInclusion(i.inclusion));
  const itemizedExpenditures = includedExpenditures.filter((i) => i.isItemized);
  const nonItemizedExpenditures = includedExpenditures.filter((i) => !i.isItemized);

  const contributionsTotal = includedContributions.reduce((s, i) => s + i.amount, 0);
  const itemizedContributionsTotal = itemizedContributions.reduce((s, i) => s + i.amount, 0);
  const nonItemizedContributionsTotal = nonItemizedContributions.reduce((s, i) => s + i.amount, 0);
  const expendituresTotal = includedExpenditures.reduce((s, i) => s + i.amount, 0);
  const itemizedExpendituresTotal = itemizedExpenditures.reduce((s, i) => s + i.amount, 0);
  const nonItemizedExpendituresTotal = nonItemizedExpenditures.reduce((s, i) => s + i.amount, 0);
  const refundsTotal = report.refundItems.reduce((s, r) => s + r.amount, 0);
  const contributionCount = includedContributions.length;

  // One combined off-filing section: every non-Included contribution + expenditure. A
  // `Pending` row reads "needs review" (off the filing because it isn't approved yet), a
  // Pending in-kind "Other" that the EDI can't carry reads "add manually in {portal}".
  const offFilingRows: OffFilingRow[] = [
    ...report.contributionItems
      .filter((i) => !isOnReportInclusion(i.inclusion))
      .map((i) => ({
        id: i.id,
        date: i.date,
        name: i.contactName,
        amount: i.amount,
        inclusion: i.inclusion,
        requiresManualPortalEntry: i.requiresManualPortalEntry,
      })),
    ...report.expenditureItems
      .filter((i) => !isOnReportInclusion(i.inclusion))
      .map((i) => ({
        id: i.id,
        date: i.date,
        name: i.contactName,
        amount: i.amount,
        inclusion: i.inclusion,
        requiresManualPortalEntry: false,
      })),
  ];

  return (
    <div className="space-y-4" data-testid="al-summary">
      {/* Statement of funds — the AL analog of the federal "Financial summary" card. */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm">
        <div className="border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-foreground">Statement of funds</h3>
          <p className="text-xs text-muted-foreground">What this report rolls up to, as it will be filed.</p>
        </div>
        <dl className="divide-y divide-slate-100">
          <FundsLine label="Contributions received" value={contributionsTotal} />
          <FundsLine label="Expenditures" value={expendituresTotal} />
          {refundsTotal !== 0 && <FundsLine label="Refunds issued" value={refundsTotal} muted />}
        </dl>
      </div>

      {/* Cash-on-hand bridge. The footing strip when the backend can reconstruct it; an
          ExternalPortal (backfill) report has no bridge, so we show the frozen beginning /
          ending anchors only. */}
      <CashBridge report={report} />

      {/* Schedule A — contributions (editable), open by default. */}
      <ScheduleSection
        code="A"
        title="Contributions"
        blurb="Itemized + non-itemized contributions received this period."
        rightSummary={itemSummary(contributionsTotal, contributionCount)}
        defaultOpen
        testId="al-schedule-a"
      >
        {isReady && preview && (
          <DualCountNote
            inPeriodCount={preview.itemized.count + preview.unreconciled.count}
            willBeOnFilingCount={preview.willFile.count}
          />
        )}
        {report.scheduleASummary.length > 0 && (
          <div className="px-4 py-2 pl-14 text-xs text-muted-foreground" data-testid="al-schedule-a-breakdown">
            {report.scheduleASummary.map((s) => `${s.type}: ${s.count} (${formatAmount(s.total)})`).join(' · ')}
          </div>
        )}
        {/* ADR-018 — the frozen non-itemized remainder (AL form line 2b) for an
            externally-filed report; itemized rows + this line equal the frozen total. */}
        {isExternallyFiled && report.nonItemizedContributions !== 0 && (
          <RemainderLine kind="contributions" amount={report.nonItemizedContributions} />
        )}
        {itemizedContributions.length === 0 && nonItemizedContributions.length === 0 ? (
          <EmptyRow />
        ) : (
          <>
            {itemizedContributions.length > 0 && (
              <div className="px-4 py-1.5 pl-14 text-xs font-medium text-muted-foreground">
                {FilingLockCopy.ItemizedContributionsLabel} — {itemizedContributions.length} item
                {itemizedContributions.length === 1 ? '' : 's'} · {formatAmount(itemizedContributionsTotal)}
              </div>
            )}
            {itemizedContributions.map((c) => (
              <ContributionRow
                key={c.id}
                item={c}
                reportId={report.id}
                portalName={portalName}
                requiresEmployer={requiresEmployer}
                onEdit={onEditContribution}
                onFixMissingInfo={onFixMissingInfo}
              />
            ))}
            {nonItemizedContributions.length > 0 && (
              <div className="px-4 py-1.5 pl-14 text-xs font-medium text-muted-foreground">
                {FilingLockCopy.NonItemizedContributionsLabel} — {nonItemizedContributions.length} item
                {nonItemizedContributions.length === 1 ? '' : 's'} · {formatAmount(nonItemizedContributionsTotal)}
              </div>
            )}
            {nonItemizedContributions.map((c) => (
              <ContributionRow
                key={c.id}
                item={c}
                reportId={report.id}
                portalName={portalName}
                requiresEmployer={requiresEmployer}
                onEdit={onEditContribution}
                onFixMissingInfo={onFixMissingInfo}
              />
            ))}
          </>
        )}
      </ScheduleSection>

      {/* Schedule B — expenditures (editable). */}
      <ScheduleSection
        code="B"
        title="Expenditures"
        blurb="Money spent this period."
        rightSummary={itemSummary(expendituresTotal, includedExpenditures.length)}
        defaultOpen={isExternallyFiled}
        testId="al-schedule-b"
      >
        {isExternallyFiled && report.nonItemizedExpenditures !== 0 && (
          <RemainderLine kind="expenditures" amount={report.nonItemizedExpenditures} />
        )}
        {includedExpenditures.length === 0 ? (
          <EmptyRow />
        ) : (
          <>
            {itemizedExpenditures.length > 0 && (
              <div className="px-4 py-1.5 pl-14 text-xs font-medium text-muted-foreground">
                {FilingLockCopy.ItemizedExpendituresLabel} — {itemizedExpenditures.length} item
                {itemizedExpenditures.length === 1 ? '' : 's'} · {formatAmount(itemizedExpendituresTotal)}
              </div>
            )}
            {itemizedExpenditures.map((e) => (
              <ExpenditureRow key={e.id} item={e} reportId={report.id} onEdit={onEditExpenditure} />
            ))}
            {nonItemizedExpenditures.length > 0 && (
              <div className="px-4 py-1.5 pl-14 text-xs font-medium text-muted-foreground">
                {FilingLockCopy.NonItemizedExpendituresLabel} — {nonItemizedExpenditures.length} item
                {nonItemizedExpenditures.length === 1 ? '' : 's'} · {formatAmount(nonItemizedExpendituresTotal)}
              </div>
            )}
            {nonItemizedExpenditures.map((e) => (
              <ExpenditureRow key={e.id} item={e} reportId={report.id} onEdit={onEditExpenditure} />
            ))}
          </>
        )}
      </ScheduleSection>

      {/* Schedule D — issued contribution refunds (read-only). */}
      {report.refundItems.length > 0 && (
        <ScheduleSection
          code="D"
          title="Refunds"
          blurb="Contributions returned to donors during this period."
          rightSummary={itemSummary(refundsTotal, report.refundItems.length)}
          defaultOpen={isExternallyFiled}
          testId="al-schedule-d"
        >
          {report.refundItems.map((r) => (
            <EntityRow
              key={r.id}
              testId={`al-d-${r.id}`}
              date={r.date}
              name={r.contactName}
              meta="Refund of contribution"
              amount={r.amount}
              onEdit={null}
            />
          ))}
        </ScheduleSection>
      )}

      <OffFilingSection rows={offFilingRows} portalName={portalName} />
    </div>
  );
}

function itemSummary(total: number, count: number): string {
  return `${formatAmount(total)} · ${count} item${count === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Cash bridge — the horizontal strip. A footing 5-cell bridge from `report.cashBridge`
// (Beginning + Received − Payments = Cash at close); a 2-cell beginning/ending strip when
// the backend can't reconstruct the split (ExternalPortal backfill).
// ---------------------------------------------------------------------------
function CashBridge({ report }: { report: ComplianceReportDetailDto }) {
  const b = report.cashBridge;
  return (
    <div
      className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm"
      data-testid="al-cash-bridge"
    >
      {b ? (
        <div className="grid grid-cols-5 divide-x divide-slate-100 text-center">
          <BridgeCell label="Beginning cash" value={b.beginning} />
          <BridgeCell label="+ Received" value={b.received} tone="pos" />
          <BridgeCell label="Subtotal" value={b.subtotal} />
          <BridgeCell label="− Payments" value={b.paid} tone="neg" />
          <BridgeCell label="Cash at close" value={b.ending} strong />
        </div>
      ) : (
        <div className="grid grid-cols-2 divide-x divide-slate-100 text-center">
          <BridgeCell label="Beginning cash" value={report.beginningBalance} />
          <BridgeCell label="Cash at close" value={report.endingBalance} strong />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expandable schedule section — the header shows code + title + a right-side subtotal; the
// body reveals on click. Mirrors the federal + VA drill-down affordance.
// ---------------------------------------------------------------------------
function ScheduleSection({
  code,
  title,
  blurb,
  rightSummary,
  defaultOpen,
  testId,
  children,
}: {
  code: string;
  title: string;
  blurb: string;
  rightSummary: string;
  defaultOpen?: boolean;
  testId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-card shadow-sm" data-testid={testId}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet-100 text-xs font-bold text-violet-700">
          {code}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="block text-xs text-muted-foreground">{blurb}</span>
        </span>
        <span className="shrink-0 text-right text-xs font-medium tabular-nums text-muted-foreground">
          {rightSummary}
        </span>
      </button>
      {open && <div className="divide-y divide-slate-50 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// A contribution row — carries the edit + fix-missing-info affordances a pre-filing report needs.
function ContributionRow({
  item,
  reportId,
  portalName,
  requiresEmployer,
  onEdit,
  onFixMissingInfo,
}: {
  item: ContributionReportItem;
  reportId: string;
  portalName: string;
  requiresEmployer: boolean;
  onEdit: ((id: string) => void) | null;
  onFixMissingInfo: (contactId: string) => void;
}) {
  // The per-row missing-employer/occupation badge. `missingEmployerInfo` is server-produced
  // through the shared donor-info verdict (the itemized, on-filing twin of the report-level
  // missing-donor-info banner), so this is a plain typed read.
  const hasMissingInfo = requiresEmployer && item.missingEmployerInfo;
  // §32 — a Confirmed in-kind "Other" row reads "will be on the filing" (a normal Included
  // row) but the AL EDI can't carry it, so it must never be a silent normal row: badge it
  // here so the treasurer knows Export skips it and it needs portal hand-entry (the
  // dedicated ManualPortalEntryPanel in the submission area lists the values to re-key).
  const needsManualPortal = item.requiresManualPortalEntry && item.ledgerStatus === 'Confirmed';
  const afterName = (
    <>
      {hasMissingInfo && (
        <button
          type="button"
          data-testid={`al-missing-info-${item.id}`}
          aria-label={`Fix missing employer/occupation for ${item.contactName}`}
          title="Missing employer/occupation — click to fix"
          onClick={(e) => {
            e.stopPropagation();
            onFixMissingInfo(item.contactId);
          }}
          className="rounded p-0.5 hover:bg-amber-100"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
        </button>
      )}
      {needsManualPortal && (
        <span
          data-testid={`contribution-manual-portal-badge-${item.id}`}
          className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
        >
          {PreflightCopy.ScheduleAddManuallyInPortalLabel.replace('{portal}', portalName)}
        </span>
      )}
      <ReportedRowBadge inclusion={item.inclusion} testId={`al-contribution-reported-badge-${item.id}`} />
    </>
  );
  // #644 — the sent-status column. A never-EDI In-Kind "Other" row reads the portal-hand-entry
  // marker (a re-send would be a dead-end); every other row reads the server's truthful sent /
  // not-yet-sent signal.
  // #647 — an already-sent EDI row also carries the deliberate re-send action here (the state may
  // have lost it, or the treasurer deleted it in the portal). A not-yet-sent row rides the next
  // export anyway, and an In-Kind "Other" row the export omits stays a portal-hand-entry dead-end —
  // neither gets the action.
  const isSentEdiRow = item.alreadySent && !item.requiresManualPortalEntry;
  const sentStatus = (
    <span className="inline-flex flex-col items-end gap-0.5">
      <SentStatusMarker
        testId={`contribution-sent-status-${item.id}`}
        label={
          item.requiresManualPortalEntry
            ? PreflightCopy.ScheduleAddManuallyInPortalLabel.replace('{portal}', portalName)
            : item.alreadySent
              ? PreflightCopy.ScheduleRowSentLabel
              : PreflightCopy.ScheduleRowNotSentLabel
        }
        tone={item.requiresManualPortalEntry ? 'portal' : item.alreadySent ? 'sent' : 'unsent'}
      />
      {isSentEdiRow && (
        <ResendTransactionAction
          reportId={reportId}
          kind="Contribution"
          transactionId={item.id}
          resendQueued={item.resendQueued}
        />
      )}
    </span>
  );
  return (
    <EntityRow
      testId={`al-a-${item.id}`}
      date={item.date}
      name={item.contactName}
      meta={`${item.contributionType} · ${electionDesignationLabel(item.electionDesignation, item.electionYear)}`}
      amount={item.amount}
      onEdit={onEdit ? () => onEdit(item.id) : null}
      afterName={afterName}
      status={sentStatus}
    />
  );
}

function ExpenditureRow({
  item,
  reportId,
  onEdit,
}: {
  item: ExpenditureReportItem;
  reportId: string;
  onEdit: ((id: string) => void) | null;
}) {
  return (
    <EntityRow
      testId={`al-b-${item.id}`}
      date={item.date}
      name={item.contactName}
      meta={`${item.category}${item.purpose ? ` · ${item.purpose}` : ''}`}
      amount={item.amount}
      onEdit={onEdit ? () => onEdit(item.id) : null}
      afterName={
        <>
          <ReportedRowBadge inclusion={item.inclusion} testId={`al-expenditure-reported-badge-${item.id}`} />
        </>
      }
      // #644 — every expenditure rides the EDI export, so the column reads sent / not-yet-sent.
      // #647 — an already-sent expenditure also carries the deliberate re-send action here.
      status={
        <span className="inline-flex flex-col items-end gap-0.5">
          <SentStatusMarker
            testId={`expenditure-sent-status-${item.id}`}
            label={
              item.alreadySent
                ? PreflightCopy.ScheduleRowSentLabel
                : PreflightCopy.ScheduleRowNotSentLabel
            }
            tone={item.alreadySent ? 'sent' : 'unsent'}
          />
          {item.alreadySent && (
            <ResendTransactionAction
              reportId={reportId}
              kind="Expenditure"
              transactionId={item.id}
              resendQueued={item.resendQueued}
            />
          )}
        </span>
      }
    />
  );
}

function EntityRow({
  date,
  name,
  meta,
  amount,
  onEdit,
  testId,
  afterName,
  status,
}: {
  date: string;
  name: string;
  meta: string;
  amount: number;
  onEdit: (() => void) | null;
  testId: string;
  afterName?: ReactNode;
  // #644 — the row's sent-status marker, fixed at a consistent position (before the amount) so it
  // reads as one added column down each schedule. Null for refund rows (contributions +
  // expenditures only).
  status?: ReactNode;
}) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 px-4 py-2 pl-14 text-sm">
      <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{formatDate(date)}</span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5 text-foreground">
          <span className="truncate">{name}</span>
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onEdit}
              aria-label={`Edit this transaction: ${name}`}
              title="Open in the ledger to edit this transaction"
              className="h-8 w-8 shrink-0 text-primary"
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
          {afterName}
        </span>
        {meta && <span className="block text-xs text-muted-foreground">{meta}</span>}
      </span>
      {status && <span className="w-32 shrink-0 text-right">{status}</span>}
      <span className="w-24 shrink-0 text-right tabular-nums text-foreground">{formatAmount(amount)}</span>
    </div>
  );
}

// #644 — the one per-row sent-status marker: the report screen's added "sent to the state?"
// column, the home the re-send action will land in. An EDI row reads the truthful sent /
// not-yet-sent signal (`item.alreadySent`, the shared already-sent union — never ledger status
// alone); a row the export can't carry (In-Kind "Other") reads the portal-hand-entry marker
// instead, since a re-send would be a dead-end for it.
function SentStatusMarker({
  testId,
  label,
  tone,
}: {
  testId: string;
  label: string;
  tone: 'sent' | 'unsent' | 'portal';
}) {
  const toneClass =
    tone === 'sent'
      ? 'text-emerald-700'
      : tone === 'portal'
        ? 'text-amber-700'
        : 'text-muted-foreground';
  return (
    <span data-testid={testId} className={`text-xs font-medium ${toneClass}`}>
      {label}
    </span>
  );
}

function EmptyRow() {
  return <div className="px-4 py-2 pl-14 text-sm text-muted-foreground">No rows on this filing.</div>;
}

// The Schedule-A dual count — "in this period" (every Pending row that hasn't been attested
// yet) vs "will be on the filing" (the will-file projection, the file's own inclusion gate).
function DualCountNote({
  inPeriodCount,
  willBeOnFilingCount,
}: {
  inPeriodCount: number;
  willBeOnFilingCount: number;
}) {
  const diff = inPeriodCount - willBeOnFilingCount;
  return (
    <div className="px-4 py-2 pl-14 text-xs" data-testid="al-dual-count">
      <div className="flex flex-wrap gap-3 text-muted-foreground">
        <span>
          In this period: {inPeriodCount} contribution{inPeriodCount === 1 ? '' : 's'}
        </span>
        <span>
          Will be on the filing: {willBeOnFilingCount} contribution{willBeOnFilingCount === 1 ? '' : 's'}
        </span>
      </div>
      {diff > 0 && (
        <p className="mt-1 text-amber-800">
          {diff} contribution{diff === 1 ? '' : 's'} need review before they&apos;ll be on the filing.
        </p>
      )}
    </div>
  );
}

// ADR-018 — the frozen non-itemized remainder line (AL form line 2b/5b) for an
// externally-filed report. Rendered with an alert style + explanatory note when negative (a
// backfill data error where the itemized rows exceed the frozen total), never hidden; a help
// line when positive. Same testids + copy as the flat view so the meaning is stable.
function RemainderLine({ kind, amount }: { kind: 'contributions' | 'expenditures'; amount: number }) {
  const negative = amount < 0;
  const label =
    kind === 'contributions'
      ? FilingLockCopy.NonItemizedContributionsLabel
      : FilingLockCopy.NonItemizedExpendituresLabel;
  return (
    <div
      // A negative remainder is a backfill data error (itemized rows exceed the frozen total) —
      // surface it as an alert (tinted + left rule), not just red text, so it reads as
      // "re-check this", matching the flat view's alert container (ADR-018, alert-don't-block).
      className={`px-4 py-2 pl-14 text-sm ${negative ? 'border-l-2 border-destructive bg-destructive/5' : ''}`}
      data-testid={`non-itemized-${kind}-line`}
    >
      <p className={`font-medium ${negative ? 'text-destructive' : 'text-foreground/80'}`}>
        {label} — <span className="tabular-nums">{formatAmount(amount)}</span>
      </p>
      {negative ? (
        <p className="mt-0.5 text-xs font-medium text-destructive" data-testid={`non-itemized-${kind}-negative-note`}>
          {FilingLockCopy.NonItemizedNegativeWarning}
        </p>
      ) : (
        <p className="mt-0.5 text-xs text-muted-foreground">{FilingLockCopy.NonItemizedHelpLine}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "In this period, not on this filing" — the excluded (Pending / Voided / status-drift) rows,
// one source, mirroring the federal + VA off-filing section.
// ---------------------------------------------------------------------------
interface OffFilingRow {
  id: string;
  date: string;
  name: string;
  amount: number;
  inclusion: ReportRowInclusion;
  requiresManualPortalEntry: boolean;
}

function offFilingBadge(
  inclusion: ReportRowInclusion,
  requiresManualPortalEntry: boolean,
  portalName: string,
): { label: string; className: string } | null {
  // A Pending in-kind "Other" the EDI can't carry: its remedy is portal hand-entry, so the
  // badge points there rather than at export + upload.
  if (inclusion === 'ExcludedPending' && requiresManualPortalEntry) {
    return {
      label: PreflightCopy.ScheduleAddManuallyInPortalLabel.replace('{portal}', portalName),
      className: 'bg-amber-100 text-amber-800',
    };
  }
  switch (inclusion) {
    case 'Included':
    case 'Reported': // on the report (folded already-reported row), not off it
      return null;
    case 'ExcludedPending':
      return { label: PreflightCopy.ScheduleOffFilingNeedsReviewLabel, className: 'bg-amber-100 text-amber-800' };
    case 'ExcludedVoided':
      return { label: PreflightCopy.ScheduleExcludedVoidedLabel, className: 'bg-muted text-muted-foreground' };
    case 'ExcludedStatusDrift':
      return { label: PreflightCopy.ScheduleExcludedStatusDriftLabel, className: 'bg-muted text-muted-foreground' };
    default:
      return assertNever(inclusion);
  }
}

function OffFilingSection({ rows, portalName }: { rows: OffFilingRow[]; portalName: string }) {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-muted bg-muted/30" data-testid="al-off-filing-section">
      <div className="px-4 py-2.5">
        <h4 className="text-sm font-semibold text-foreground">In this period, not on this filing</h4>
        <p className="text-xs text-muted-foreground">
          These are in the period but off the filing — they don&apos;t count toward the totals above.
        </p>
      </div>
      <div className="divide-y divide-slate-100 border-t border-slate-100">
        {rows.map((row) => {
          const badge = offFilingBadge(row.inclusion, row.requiresManualPortalEntry, portalName);
          return (
            <div
              key={row.id}
              data-testid={`al-off-filing-${row.id}`}
              className="flex items-baseline gap-2 px-4 py-1.5 text-sm text-muted-foreground"
            >
              <span className="w-20 shrink-0 tabular-nums">{formatDate(row.date)}</span>
              <span className="min-w-0 flex-1 truncate">{row.name}</span>
              <span className="w-24 shrink-0 text-right tabular-nums">{formatAmount(row.amount)}</span>
              {badge && (
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                  {badge.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FundsLine({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between px-4 py-2 text-sm">
      <dt className={muted ? 'text-muted-foreground' : 'text-foreground/80'}>{label}</dt>
      <dd className={`tabular-nums ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{formatAmount(value)}</dd>
    </div>
  );
}

function BridgeCell({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: number;
  strong?: boolean;
  tone?: 'pos' | 'neg';
}) {
  const valueColor = strong
    ? 'text-foreground'
    : tone === 'pos'
      ? 'text-emerald-600'
      : tone === 'neg'
        ? 'text-rose-600'
        : 'text-foreground/70';
  return (
    <div className="px-2 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 tabular-nums ${strong ? 'text-base font-bold' : 'text-sm font-medium'} ${valueColor}`}>
        {formatAmount(value)}
      </div>
    </div>
  );
}
